import { ChartBarIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import type { LedgerType } from '@/api/types';

interface AggregatedEmptyStateProps {
    /** Whether there are any filters applied */
    hasFilters: boolean;
    /** Callback to reset filters */
    onResetFilters?: () => void;
    /** Ledger type for proper messaging */
    type: LedgerType;
    /** Whether the data is still loading */
    isLoading?: boolean;
    /** Minimum height to match chart container and prevent layout shift */
    minHeight?: number;
    /** Callback to switch to list view for adding data */
    onSwitchToList?: () => void;
}

/**
 * Empty state component for the aggregated values chart.
 * Shows different messages based on whether filters are applied.
 */
export function AggregatedEmptyState({
    hasFilters,
    onResetFilters,
    type,
    isLoading = false,
    minHeight,
    onSwitchToList,
}: AggregatedEmptyStateProps) {
    const typeLabel = type === 'asset' ? 'assets' : 'debts';
    const typeLabelSingular = type === 'asset' ? 'asset' : 'debt';
    const containerStyle = minHeight ? { minHeight } : undefined;

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center" style={containerStyle}>
                <div className="animate-pulse">
                    <ChartBarIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    Loading chart data...
                </p>
            </div>
        );
    }

    if (hasFilters) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center" style={containerStyle}>
                <AdjustmentsHorizontalIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                    No matching {typeLabel}
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                    No {typeLabel} match the current filter criteria.
                    Try adjusting your filters or selecting different collections/tags.
                </p>
                {onResetFilters && (
                    <button
                        onClick={onResetFilters}
                        className="mt-4 px-4 py-2 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                    >
                        Reset Filters
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center py-12 text-center" style={containerStyle}>
            <ChartBarIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No data available
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                Add some {typeLabel} with historical values to see aggregated analytics.
            </p>
            {onSwitchToList && (
                <button
                    onClick={onSwitchToList}
                    className="mt-4 text-accent hover:text-accent/80 transition-colors text-sm"
                >
                    Add your first {typeLabelSingular} →
                </button>
            )}
        </div>
    );
}
